import type React from "react"
import "./ContestRules.scss"
import { gettext, interpolate } from "@multimediallc/web-utils"
import { useAppContext } from "../../../hooks/appContext"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { normalizeResource } from "../../../routes/util"
import { Link, Typography } from "../../common"

const i18n = {
    beginDateText: gettext("Begin Date: June 15th, 2011"),
    contestTermsText: (languageCode: string): string => {
        return interpolate(
            gettext(
                'Contest terms are listed in <a class="link hrefColor" href="%(link)s">Appendix A</a> of our terms',
            ),
            { link: normalizeResource(languageCode, "/terms/appendix-a") },
            true,
        )
    },
    contestRulesCapsText: gettext("CONTEST RULES"),
    contestRulesDescriptionText: gettext(
        "You must be 18 years or older and have an account on Chaturbate. Payments are available to claim once your account reaches a minimum of $50. To receive prize money, simply click 'Claim Your Winnings' and complete a claim form. Cash winners are responsible for all taxes and must supply legal name, photo identification, mailing address, birth date, telephone number and social security number.",
    ),
    howDoPointsWorkText: gettext("How do points work?"),
    howDoPointsWorkDescriptionTop: gettext(
        "Points are awarded every sixty (60) seconds, so it's easy to quickly get in on the action! If you are one of the top ten most viewed broadcasters in a given minute, you'll earn points! You can earn anywhere from 1-10 points per minute based on the amount of unique registered viewers that watch your cam for at least a minute. More viewers equals more points!",
    ),
    howDoPointsWorkDescriptionBottom: gettext(
        "At the end of the hour, the broadcaster who has the most points wins $10 cash! The broadcaster with the 2nd most points wins $5 cash! All points are then reset and a new contest begins. Keep in mind that anonymous users who are viewing your cam do not count towards top ten rankings, so encourage them to register! The maximum amount of points achievable in an hour is six hundred (600).",
    ),
    pointsListText: (rank: string, pointsPerMinute: number): string => {
        return interpolate(
            gettext(
                "The %(rank)s most viewed broadcaster earns %(points)s points per minute",
            ),
            { rank: rank, points: pointsPerMinute },
            true,
        )
    },
    whoCanWinText: gettext("Who can win?"),
    whoCanWinDescription: gettext(
        "Everyone! Girls, guys, couples, and trans users who can attract the most viewers in an hour will win prize money. You can win money around the clock as we award prizes 24 hours a day, 7 days a week!",
    ),
    howManyTimesICanWinText: gettext("How many times can I win?"),
    howManyTimesICanWinDescription: gettext(
        "There is no limit to the amount of times a broadcaster can win prize money. As long as you maintain the highest points within the hour you will win cash!",
    ),
    howDoIGetPaidText: gettext("How do I get paid?"),
    howDoIGetPaidDescription: gettext(
        "Once you reach the minimum payment and complete a claim form, you can choose to be paid via check or bank wire. All payments are processed once per month and are sent on or before the 7th of each month.",
    ),
    whatsOnTheClaimFormText: gettext("What's on the claim form?"),
    whatsOnTheClaimFormDescription: gettext(
        "To process your payment we require your legal name, mailing address, birth date, telephone number and social security number.",
    ),
    whenCanIGetStartedText: gettext("When can I get started?"),
    whenCanIGetStartedDescription: gettext(
        "Click Broadcast and start earning points right now! Good luck!",
    ),
    previousContestRulesText: gettext("Previous Contest Rules"),
}

export function ContestRules() {
    const languageCode = useAppContext().context.language_code

    const createParagraph = (
        text: React.ReactNode | string,
    ): React.ReactNode => {
        return (
            <Typography size={Sizes.xs} component="p">
                {text}
            </Typography>
        )
    }

    const createHeading = (text: string): React.ReactNode => {
        return (
            <Typography
                size={Sizes.md}
                weight={Weights.medium}
                component="h2"
                className="heading"
            >
                {text}
            </Typography>
        )
    }

    const createPointsList = (): React.ReactNode => {
        const rankAndPoints = [
            { rank: "", points: 10 },
            { rank: "2nd", points: 9 },
            { rank: "3rd", points: 8 },
            { rank: "4th", points: 7 },
            { rank: "5th", points: 6 },
            { rank: "6th", points: 5 },
            { rank: "7th", points: 4 },
            { rank: "8th", points: 3 },
            { rank: "9th", points: 2 },
            { rank: "10th", points: 1 },
        ]
        return (
            <ul>
                {rankAndPoints.map(({ rank, points }) => (
                    <Typography size={Sizes.xs} component="li" key={points}>
                        {i18n.pointsListText(rank, points)}
                    </Typography>
                ))}
            </ul>
        )
    }

    return (
        <div className="ContestRules">
            {createParagraph(i18n.beginDateText)}
            <Typography
                size={Sizes.xs}
                component="p"
                dangerouslySetInnerHTML={{
                    __html: i18n.contestTermsText(languageCode),
                }}
            />
            {createParagraph(
                <>
                    <strong>{i18n.contestRulesCapsText}</strong>
                    {": "}
                    {i18n.contestRulesDescriptionText}
                </>,
            )}
            {createHeading(i18n.howDoPointsWorkText)}
            {createParagraph(i18n.howDoPointsWorkDescriptionTop)}
            {createPointsList()}
            {createParagraph(i18n.howDoPointsWorkDescriptionBottom)}
            {createHeading(i18n.whoCanWinText)}
            {createParagraph(i18n.whoCanWinDescription)}
            {createHeading(i18n.howManyTimesICanWinText)}
            {createParagraph(i18n.howManyTimesICanWinDescription)}
            {createHeading(i18n.howDoIGetPaidText)}
            {createParagraph(i18n.howDoIGetPaidDescription)}
            {createHeading(i18n.whatsOnTheClaimFormText)}
            {createParagraph(i18n.whatsOnTheClaimFormDescription)}
            {createHeading(i18n.whenCanIGetStartedText)}
            {createParagraph(i18n.whenCanIGetStartedDescription)}
            <hr className="endLine" />
            <Link href="/contest/previous_contest_rules/">
                {i18n.previousContestRulesText}
            </Link>
        </div>
    )
}
